import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, ShieldCheck, CheckCircle2, EyeOff, Eye } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => { if (!token) setMissing(true); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset — sign in with your new password");
      setTimeout(() => nav("/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen paper-bg grid md:grid-cols-2">
      {/* Left panel (matches Login page) */}
      <div className="hidden md:flex flex-col justify-between p-10 lg:p-12 bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -left-20 bottom-0 w-80 h-80 rounded-full bg-amber-300/20 blur-3xl" />
        <div className="relative flex items-center gap-2">
          <img src="/logo.png" alt="Print Plus" className="w-10 h-10 object-contain rounded-lg bg-white/15 backdrop-blur p-0.5" />
          <div className="leading-tight">
            <span className="font-display text-xl font-bold block">Print Plus</span>
            <span className="text-[10px] uppercase tracking-widest text-white/80 font-semibold">by AI Upaya</span>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl lg:text-5xl font-display font-black tracking-tighter leading-[1.05] mb-5">Pick a fresh password.</h1>
          <p className="text-white/85 text-lg max-w-md">Choose something you'll remember — at least 6 characters. You'll be signed in on the next screen.</p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/15 px-3 py-1.5 text-xs text-white/90">
            <ShieldCheck className="w-3.5 h-3.5" />Reset link is single-use and expires in 24 hours
          </div>
        </div>
        <div className="relative text-sm text-white/70">© 2026 Print Plus by AI Upaya</div>
      </div>

      {/* Right panel */}
      <div className="flex flex-col md:justify-center px-5 py-8 md:p-6 relative">
        <div className="md:hidden flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Print Plus" className="w-8 h-8 object-contain" />
            <span className="font-display font-bold">Print Plus <span className="text-[9px] uppercase tracking-widest text-orange-500 font-semibold">· by AI Upaya</span></span>
          </Link>
          <Link to="/login" className="text-xs text-slate-500 hover:text-orange-600 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" />Sign in</Link>
        </div>

        <div className="w-full max-w-sm mx-auto">
          {missing ? (
            <div className="space-y-5 bg-white p-7 md:p-8 rounded-2xl border border-slate-200 shadow-sm md:shadow-lg" data-testid="reset-missing">
              <div className="w-10 h-10 rounded-xl bg-red-100 grid place-items-center"><KeyRound className="w-5 h-5 text-red-600" /></div>
              <div>
                <h2 className="font-display text-2xl font-bold">Reset link missing</h2>
                <p className="text-sm text-slate-500 mt-1">This page opens from the "Reset password" link in the email we sent you. Please open that email again, or request a new link.</p>
              </div>
              <Link to="/login"><Button className="w-full bg-orange-500 hover:bg-orange-600 h-11 font-semibold" data-testid="back-to-login">Back to sign in</Button></Link>
            </div>
          ) : done ? (
            <div className="space-y-5 bg-white p-7 md:p-8 rounded-2xl border border-emerald-200 shadow-sm md:shadow-lg" data-testid="reset-done">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <h2 className="font-display text-2xl font-bold">Password reset</h2>
                <p className="text-sm text-slate-500 mt-1">Redirecting you to sign in…</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 bg-white p-7 md:p-8 rounded-2xl border border-slate-200 shadow-sm md:shadow-lg" data-testid="reset-form">
              <div>
                <div className="w-10 h-10 rounded-xl bg-orange-100 grid place-items-center mb-3"><KeyRound className="w-5 h-5 text-orange-600" /></div>
                <h2 className="font-display text-2xl font-bold">Choose a new password</h2>
                <p className="text-sm text-slate-500 mt-1">At least 6 characters. You'll be signed in on the next step.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw">New password</Label>
                <div className="relative">
                  <Input id="pw" type={show ? "text" : "password"} required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw" className="h-11 pr-10" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center text-slate-400 hover:text-slate-700" data-testid="toggle-pw">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw2">Confirm new password</Label>
                <Input id="pw2" type={show ? "text" : "password"} required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2" className="h-11" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 h-11 font-semibold" data-testid="reset-submit">
                {loading ? "Updating…" : "Reset password"}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-xs text-slate-500 hover:text-orange-600 inline-flex items-center gap-1" data-testid="back-signin"><ArrowLeft className="w-3 h-3" />Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
